import {InputAdornment} from '@mui/material';
import AppIconButton from 'components/buttons/AppIconButton';
import {Eye, EyeSlash} from 'iconsax-react';
import {useState} from 'react';
import {
    Controller,
    ControllerProps,
    FieldPath,
    FieldValues,
    PathValue,
    RegisterOptions,
    ValidationRule,
} from 'react-hook-form';
import AppInput, {AppInputProps} from './AppInput';
export type AppControlledInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppInputProps, 'name' | 'required'> &
    Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
        errorMessage?: string;
        required?: string | ValidationRule<boolean>;
        isEmail?: boolean;
        label: string | React.ReactNode;
        defaultValue?: string;
        withCustomSetValue?: boolean;
    };

export function AppControlledInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    rules,
    defaultValue = '' as PathValue<TFieldValues, TName> & string,
    value,
    required,
    errorMessage,
    isEmail,
    label,
    withCustomSetValue = false,
    ...rest
}: AppControlledInputProps<TFieldValues, TName>) {
    const [showPassword, setShowPassowrd] = useState(false);
    const customeRules: Omit<
        RegisterOptions<TFieldValues, TName>,
        'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
    > = rules
        ? rules
        : {
              required: required ? `${label} is required` : false,
              pattern:
                  required && isEmail
                      ? {value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, message: 'Invalid email address'}
                      : undefined,
          };
    return (
        <>
            <Controller
                name={name}
                rules={customeRules}
                control={control}
                {...rest}
                defaultValue={defaultValue}
                render={({field: {onChange, ...fieldData}, fieldState: {invalid, error}}) => {
                    return (
                        <>
                            <AppInput
                                label={label}
                                required={!!required}
                                error={invalid}
                                helperText={errorMessage ?? error?.message ?? undefined}
                                {...rest}
                                {...fieldData}
                                type={showPassword ? 'text' : rest.type}
                                InputProps={{
                                    endAdornment:
                                        rest.type === 'password' ? (
                                            <InputAdornment position="end">
                                                <AppIconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => {
                                                        setShowPassowrd(!showPassword);
                                                    }}
                                                    edge="end"
                                                >
                                                    {showPassword ? <EyeSlash /> : <Eye />}
                                                </AppIconButton>
                                            </InputAdornment>
                                        ) : undefined,
                                }}
                                onChange={e => {
                                    if (rest.type === 'number' && typeof e.target.value === 'string') {
                                        // If the input type is number, we need to parse the value to a number

                                        const parsedValue = parseFloat(e.target.value);
                                        if (!isNaN(parsedValue)) {
                                            onChange(parsedValue);
                                        } else {
                                            onChange(null); // Reset to empty string if parsing fails
                                        }
                                    } else if (!withCustomSetValue) {
                                        onChange(e.target.value);
                                    } else {
                                        onChange(e);
                                    }
                                    if (rest.onChange) rest.onChange(e);
                                }}
                            />
                        </>
                    );
                }}
            />
        </>
    );
}

export default AppControlledInput;
